/**
 * FinancingReadinessScore — Global FRS Widget
 *
 * Deterministic, traceable financing readiness scoring.
 * Modes: "kpi" (compact card) | "panel" (full breakdown) | "inline" (score + band only)
 *
 * Usage:
 *   <FinancingReadinessScore assessmentId={id} mode="panel" />
 *   <FinancingReadinessScore assessmentId={id} mode="kpi" />
 */
import React, { useState, useEffect, useCallback } from "react";
import client from "@/api/client";
import { TrendingUp, RefreshCw, AlertTriangle, CheckCircle2, Info } from "lucide-react";

const BAND_CONFIG = {
  high:     { color:"#059669", bg:"bg-emerald-50", border:"border-emerald-200", text:"text-emerald-700", label:"High" },
  medium:   { color:"#3B82F6", bg:"bg-blue-50",    border:"border-blue-200",    text:"text-blue-700",   label:"Medium" },
  low:      { color:"#F59E0B", bg:"bg-amber-50",   border:"border-amber-200",   text:"text-amber-700",  label:"Low" },
  very_low: { color:"#EF4444", bg:"bg-red-50",     border:"border-red-200",     text:"text-red-700",    label:"Very Low" },
};
const PRIORITY_STYLE = {
  high:   "bg-red-50 border-red-200 text-red-700",
  medium: "bg-amber-50 border-amber-200 text-amber-700",
  low:    "bg-slate-50 border-slate-200 text-slate-600",
};
const fmt = (n, d = 1) => n != null && !isNaN(n) ? (+n).toFixed(d) : "—";
const fmtK = (n) => !n ? "—" : n >= 1e3 ? `$${(n/1e3).toFixed(0)}k` : `$${Math.round(n)}`;

// ── Distance to Target ────────────────────────────────────────────────────
function DistanceToTarget({ distance }) {
  if (!distance || distance.at_target) return (
    <div className="flex items-center gap-2 px-3 py-2.5 bg-emerald-50 border border-emerald-200 rounded-xl text-xs text-emerald-700 mt-3">
      <CheckCircle2 size={13} /> You have reached Investment Grade (70+). Ready for financing.
    </div>
  );

  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid="distance-to-target">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">Distance to Investment Grade</p>
      <div className="flex items-center gap-3 mb-3">
        <div className="flex-1 h-2 bg-slate-100 rounded-full overflow-hidden">
          <div className="h-full rounded-full bg-gradient-to-r from-amber-400 to-emerald-500 transition-all"
            style={{width:`${Math.min(100,(distance.current/70)*100)}%`}} />
        </div>
        <span className="text-xs font-bold text-slate-600 tabular-nums flex-shrink-0">
          {fmt(distance.current,0)} / {distance.target} (+{fmt(distance.gap,0)} needed)
        </span>
      </div>
      <div className="space-y-1.5">
        {(distance.gap_actions||[]).map((a,i) => (
          <div key={i} className="flex items-center justify-between px-3 py-2 bg-slate-50 rounded-lg border border-slate-100">
            <span className="text-xs text-slate-600">{a.action}</span>
            <span className="text-xs font-bold ml-2 flex-shrink-0" style={{color:"#059669"}}>+{a.estimated_gain}pts</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── What-If Simulation ────────────────────────────────────────────────────
function Simulation({ scenarios, currentScore, grade }) {
  if (!scenarios || scenarios.length === 0) return null;
  return (
    <div className="mt-4 border-t border-slate-100 pt-4" data-testid="frs-simulation">
      <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-2">What If I Improve?</p>
      <div className="space-y-2">
        {scenarios.map((s,i) => {
          const bandCfg = BAND_CONFIG[s.band] || BAND_CONFIG.medium;
          return (
            <div key={i} className="flex items-start gap-3 p-3 bg-white border border-slate-200 rounded-xl">
              <div className={`flex-shrink-0 px-2 py-0.5 rounded text-xs font-bold ${bandCfg.bg} ${bandCfg.text}`}>
                {s.grade}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-[#0F172A]">{s.scenario}</p>
                <p className="text-xs text-slate-400 mt-0.5">{s.description}</p>
              </div>
              <div className="text-right flex-shrink-0 ml-2">
                <p className="text-sm font-bold" style={{color: bandCfg.color}}>{fmt(s.new_score,0)}</p>
                <p className="text-[10px] text-emerald-600 font-semibold">+{fmt(s.delta,1)}pts</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Component Bars ────────────────────────────────────────────────────────
function CompBar({ label, score, max, pct, color }) {
  return (
    <div>
      <div className="flex justify-between mb-1">
        <span className="text-xs text-slate-500">{label}</span>
        <span className="text-xs font-bold tabular-nums" style={{color}}>{score}/{max}</span>
      </div>
      <div className="relative h-2.5 bg-slate-100 rounded-full overflow-hidden">
        <div className="h-full rounded-full transition-all duration-700"
          style={{width:`${pct}%`, backgroundColor: color}} />
        <div className="absolute right-0 top-0 h-full w-px bg-slate-200" />
      </div>
    </div>
  );
}

// ── Expandable Detail Panel ───────────────────────────────────────────────
function DetailPanel({ data }) {
  const [expanded, setExpanded] = useState(false);
  const comps = data.components || {};

  const compList = [
    { key:"financial",       color:"#0F172A" },
    { key:"data_credibility",color:"#3B82F6" },
    { key:"verification",    color:"#6366F1" },
    { key:"feasibility",     color:"#F59E0B" },
    { key:"market_alignment",color:"#10B981" },
  ];

  return (
    <div className="border-t border-slate-100 pt-4 mt-4" data-testid="frs-detail-panel">
      <button onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-1.5 text-xs text-slate-400 hover:text-slate-700 mb-3 transition-colors">
        <span className={`transition-transform ${expanded ? "rotate-90" : ""}`}>▶</span>
        {expanded ? "Hide" : "Show"} score breakdown
      </button>

      {expanded && (
        <div className="space-y-3 mb-4">
          {compList.map(({ key, color }) => {
            const comp = comps[key];
            if (!comp) return null;
            return (
              <div key={key}>
                <CompBar label={comp.label} score={comp.score} max={comp.max} pct={comp.pct} color={color} />
                {key === "financial" && comp.sub && (
                  <div className="pl-3 mt-1.5 space-y-0.5">
                    {[["payback","Payback"],["roi","ROI"],["savings","Savings"]].map(([sk,sl]) => (
                      <div key={sk} className="flex justify-between text-[10px] text-slate-400">
                        <span>{sl}: {comp.sub[sk]?.desc}</span>
                        <span className="tabular-nums font-medium">{comp.sub[sk]?.score}/{comp.sub[sk]?.max}</span>
                      </div>
                    ))}
                  </div>
                )}
                {comp.desc && key !== "financial" && (
                  <p className="text-[10px] text-slate-400 mt-0.5 pl-1">{comp.desc}</p>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Recommendations */}
      {(data.recommendations || []).length > 0 && (
        <div>
          <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">
            To improve your score:
          </p>
          <div className="space-y-2">
            {data.recommendations.map((r, i) => (
              <div key={i} className={`flex items-start gap-2 px-3 py-2.5 rounded-xl border text-xs ${PRIORITY_STYLE[r.priority]||PRIORITY_STYLE.low}`}>
                <div className="flex-shrink-0 mt-0.5">
                  {r.priority === "high"
                    ? <AlertTriangle size={12} />
                    : <Info size={12} />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="leading-relaxed">{r.action}</p>
                  {r.potential_gain && (
                    <p className="mt-1 font-semibold opacity-75">{r.potential_gain}</p>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Distance to Investment Grade */}
      <DistanceToTarget distance={data.distance_to_target} />

      {/* What-if simulation */}
      <Simulation scenarios={data.simulation} currentScore={data.score} grade={data.grade} />

      <p className="text-[10px] text-slate-400 italic mt-3">{data.methodology}</p>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────
export default function FinancingReadinessScore({ assessmentId, mode = "panel" }) {
  const [data,     setData]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [refreshing,setRefresh] = useState(false);

  const load = useCallback(async (force = false) => {
    if (!assessmentId) return;
    if (force) setRefresh(true); else setLoading(true);
    try {
      const endpoint = force
        ? `/financing/readiness/recompute?assessment_id=${assessmentId}`
        : `/financing/readiness/${assessmentId}`;
      const res = force
        ? await client.post(endpoint)
        : await client.get(endpoint);
      setData(res.data);
    } catch {}
    setLoading(false);
    setRefresh(false);
  }, [assessmentId]);

  useEffect(() => { load(); }, [load]);

  if (!assessmentId) return null;

  if (loading) return (
    <div className={`${mode === "kpi" ? "h-28" : "h-24"} bg-slate-100 rounded-xl animate-pulse`}
      data-testid="frs-loading" />
  );
  if (!data) return null;

  const band  = BAND_CONFIG[data.band] || BAND_CONFIG.medium;
  const score = data.score || 0;
  const top_rec = (data.recommendations || [])[0];

  // ── KPI mode: compact card (for Dashboard KPI strip) ──────────────────
  if (mode === "kpi") {
    return (
      <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="frs-kpi-card">
        <div className="flex items-start justify-between mb-3">
          <div className={`w-8 h-8 ${band.bg} rounded-lg flex items-center justify-center`}>
            <TrendingUp size={14} style={{color: band.color}} />
          </div>
          <button onClick={() => load(true)} disabled={refreshing}
            className="text-slate-300 hover:text-slate-500">
            <RefreshCw size={11} className={refreshing ? "animate-spin" : ""} />
          </button>
        </div>
        <div className="flex items-end gap-1 mb-1">
          <span className="text-3xl font-bold tabular-nums leading-none"
            style={{color: band.color}} data-testid="frs-score">{fmt(score, 0)}</span>
          <span className="text-xs text-slate-400 mb-0.5">/ 100</span>
        </div>
        <p className="text-xs text-slate-400 uppercase tracking-wide">Financing Readiness</p>
        <span className={`inline-flex items-center mt-1.5 px-2 py-0.5 rounded-full text-[10px] font-semibold border ${band.bg} ${band.text} ${band.border}`}>
          {data.grade} · {band.label}
        </span>
        {top_rec && (
          <p className="text-[10px] text-slate-400 mt-2 line-clamp-2 leading-relaxed">{top_rec.action.slice(0, 70)}...</p>
        )}
      </div>
    );
  }

  // ── Inline mode: just score + band chip ──────────────────────────────
  if (mode === "inline") {
    return (
      <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-xl text-sm font-semibold border ${band.bg} ${band.text} ${band.border}`}
        data-testid="frs-inline">
        <TrendingUp size={13} />
        FRS {fmt(score, 0)} · {data.grade}
      </span>
    );
  }

  // ── Panel mode: full breakdown card ──────────────────────────────────
  const comps = data.components || {};
  const compList = [
    { key:"financial",       color:"#0F172A", bar_color:"bg-slate-800" },
    { key:"data_credibility",color:"#3B82F6", bar_color:"bg-blue-500"  },
    { key:"verification",    color:"#6366F1", bar_color:"bg-indigo-500"},
    { key:"feasibility",     color:"#F59E0B", bar_color:"bg-amber-500" },
    { key:"market_alignment",color:"#10B981", bar_color:"bg-teal-500"  },
  ];

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5" data-testid="frs-panel">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-bold text-[#0F172A]">Financing Readiness</h3>
            <span className={`text-xs px-2 py-0.5 rounded-full font-semibold border ${band.bg} ${band.text} ${band.border}`}>
              {data.grade} · {band.label}
            </span>
          </div>
          <p className="text-xs text-slate-400 mt-0.5">{data.probability?.label}</p>
        </div>
        <div className="text-right flex-shrink-0 ml-4">
          <div className="flex items-end gap-1">
            <span className="text-3xl font-bold tabular-nums" style={{color: band.color}}
              data-testid="frs-panel-score">{fmt(score, 0)}</span>
            <span className="text-sm text-slate-400 mb-0.5">/ 100</span>
          </div>
          <button onClick={() => load(true)} disabled={refreshing}
            className="text-xs text-slate-400 hover:text-slate-600 flex items-center gap-1 ml-auto">
            <RefreshCw size={10} className={refreshing ? "animate-spin" : ""} />
            {refreshing ? "..." : "Recompute"}
          </button>
        </div>
      </div>

      {/* Component bars */}
      <div className="space-y-2.5 mb-1" data-testid="frs-component-bars">
        {compList.map(({ key, color }) => {
          const comp = comps[key];
          if (!comp) return null;
          return (
            <CompBar key={key}
              label={comp.label}
              score={comp.score}
              max={comp.max}
              pct={comp.pct}
              color={color} />
          );
        })}
      </div>

      {/* Expandable detail + recommendations */}
      <DetailPanel data={data} />
    </div>
  );
}
